import {
  addMonths,
  addWeeks,
  endOfDay as endDay,
  format,
  getISOWeekYear,
  getISOWeeksInYear,
  isBefore,
  isFuture,
  isPast,
  isSameDay,
  isWeekend,
  nextMonday,
  parseISO,
  setISOWeek,
  startOfDay as startDay,
  startOfWeek,
  startOfYear,
  subWeeks,
} from 'date-fns'

const DATE_FORMAT = 'dd-LL-yyyy'
const DATETIME_FORMAT = 'dd-LL-yyyy HH:mm:ss'

type DateValue = Date | string | number
type Empty = null | undefined

const formatDate = (date?: DateValue | Empty): string =>
  date ? format(new Date(date), DATE_FORMAT) : ''
const formatDateTime = (date?: DateValue | Empty): string =>
  date ? format(new Date(date), DATETIME_FORMAT) : ''

function startOfDay(date: DateValue): Date
function startOfDay(date?: DateValue | Empty): Date | null
function startOfDay(date?: DateValue | Empty) {
  return date ? startDay(new Date(date)) : null
}

function endOfDay(date: DateValue): Date
function endOfDay(date?: DateValue | Empty): Date | null
function endOfDay(date?: DateValue | Empty) {
  return date ? endDay(new Date(date)) : null
}

const pastDay = (date?: DateValue | Empty) => !!date && isPast(new Date(date))
const futureDay = (date?: DateValue | Empty) =>
  !!date && isFuture(new Date(date))
const isToday = (date?: DateValue | Empty) =>
  !!date && isSameDay(new Date(date), new Date())

function skipWeekend(date: DateValue): Date
function skipWeekend(date?: DateValue | Empty): Date | null
function skipWeekend(date?: DateValue | Empty) {
  if (!date) return null

  const d = new Date(date)

  return isWeekend(d) ? nextMonday(d) : d
}

const formatWeekYearToISOWeek = (
  year?: Date | Empty,
  week?: number | Empty,
): string =>
  year && week
    ? format(setISOWeek(year.setMonth(2), week), "RRRR-'W'II") // eslint-disable-line
    : ''
const formatDateToISOWeek = (date?: DateValue | Empty): string =>
  date ? format(new Date(date), "RRRR-'W'II") : ''

const beginningOfYearDateForISOWeek = (dateString?: string) =>
  dateString
    ? new Date(getISOWeekYear(parseISO(dateString)).toString())
    : new Date()

const subtractWeeks = (date: unknown, weeks: number): Date | null =>
  isDate(date) ? subWeeks(date, weeks) : null

const isDate = (date: any): date is Date => {
  return date instanceof Date && !isNaN(date.getTime())
}

const MID_YEAR_MONTH = 6

// Number of ISO weeks (52 or 53) in an ISO week-numbering year. Uses a date in
// the middle of the year, so it can't fall in the previous or next ISO year
const weeksInISOYear = (year: number) =>
  getISOWeeksInYear(new Date(year, MID_YEAR_MONTH))

const isValidISOWeek = (week: any): boolean => {
  if (!week || typeof week !== 'string') return false

  return /^\d{4}-W\d{2}$/.test(week)
}

export {
  addWeeks,
  endOfDay,
  formatDate,
  formatDateTime,
  formatDateToISOWeek,
  formatWeekYearToISOWeek,
  futureDay,
  isBefore,
  isToday,
  isValidISOWeek,
  parseISO,
  pastDay,
  skipWeekend,
  startOfDay,
  subtractWeeks,
  beginningOfYearDateForISOWeek,
  isDate,
  addMonths,
  startOfWeek,
  startOfYear,
  weeksInISOYear,
}
